import React, { useEffect, useRef } from "react";

const MIN = 8;
const HANDLES = ["nw", "n", "ne", "e", "se", "s", "sw", "w"];
const POS = {
  nw: { left: 0, top: 0, cursor: "nwse-resize" }, n: { left: "50%", top: 0, cursor: "ns-resize" },
  ne: { left: "100%", top: 0, cursor: "nesw-resize" }, e: { left: "100%", top: "50%", cursor: "ew-resize" },
  se: { left: "100%", top: "100%", cursor: "nwse-resize" }, s: { left: "50%", top: "100%", cursor: "ns-resize" },
  sw: { left: 0, top: "100%", cursor: "nesw-resize" }, w: { left: 0, top: "50%", cursor: "ew-resize" },
};

// ใหญ่สุดที่มีสัดส่วน ratio อยู่กลางภาพ
export const fitRatioRect = (W, H, ratio) => {
  let w = W;
  let h = w / ratio;
  if (h > H) { h = H; w = h * ratio; }
  return { x: (W - w) / 2, y: (H - h) / 2, w, h };
};

// กรอบครอปบนภาพ (พิกัดเป็นพิกเซลของภาพจริง) ratio = null (อิสระ) หรือ w/h
export default function CropOverlay({ preview, width, height, zoom, rect, ratio, onChange }) {
  const canvasRef = useRef(null);
  const dragRef = useRef(null);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c || !preview) return;
    c.width = preview.width;
    c.height = preview.height;
    c.getContext("2d").drawImage(preview, 0, 0);
  }, [preview]);

  const begin = (event, mode) => {
    event.stopPropagation();
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = { mode, sx: event.clientX, sy: event.clientY, r0: { ...rect } };
  };

  const move = (event) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = (event.clientX - d.sx) / zoom;
    const dy = (event.clientY - d.sy) / zoom;
    const r0 = d.r0;

    if (d.mode === "move") {
      onChange({
        ...r0,
        x: Math.min(width - r0.w, Math.max(0, r0.x + dx)),
        y: Math.min(height - r0.h, Math.max(0, r0.y + dy)),
      });
      return;
    }

    let left = r0.x, right = r0.x + r0.w, top = r0.y, bottom = r0.y + r0.h;
    if (d.mode.includes("w")) left = Math.min(right - MIN, Math.max(0, left + dx));
    if (d.mode.includes("e")) right = Math.max(left + MIN, Math.min(width, right + dx));
    if (d.mode.includes("n")) top = Math.min(bottom - MIN, Math.max(0, top + dy));
    if (d.mode.includes("s")) bottom = Math.max(top + MIN, Math.min(height, bottom + dy));

    if (ratio) {
      const horizontal = d.mode.includes("w") || d.mode.includes("e");
      const vertical = d.mode.includes("n") || d.mode.includes("s");
      let w = right - left;
      let h = bottom - top;
      if (horizontal) h = w / ratio; else w = h * ratio;
      if (horizontal && vertical === false) {
        // ขอบซ้าย/ขวา: ขยายความสูงรอบกึ่งกลางเดิม
        const cy = r0.y + r0.h / 2;
        top = cy - h / 2; bottom = cy + h / 2;
      } else if (!horizontal) {
        const cx = r0.x + r0.w / 2;
        left = cx - w / 2; right = cx + w / 2;
      } else {
        // มุม: ตรึงมุมตรงข้าม
        if (d.mode.includes("n")) top = bottom - h; else bottom = top + h;
      }
      // ล้นขอบภาพ -> หดลงตามสัดส่วนโดยตรึงจุดอ้างอิง
      const overW = Math.max(0, -left, right - width);
      const overH = Math.max(0, -top, bottom - height);
      if (overW > 0 || overH > 0) {
        const k = Math.min((w - overW * 2 > 0 ? (w - overW * 2) / w : 0.01), (h - overH * 2 > 0 ? (h - overH * 2) / h : 0.01));
        const cx = (left + right) / 2, cy = (top + bottom) / 2;
        w *= k; h *= k;
        left = Math.max(0, Math.min(width - w, cx - w / 2)); right = left + w;
        top = Math.max(0, Math.min(height - h, cy - h / 2)); bottom = top + h;
      }
    }
    onChange({ x: left, y: top, w: right - left, h: bottom - top });
  };

  const end = () => { dragRef.current = null; };

  return (
    <div style={{ position: "relative", width: width * zoom, height: height * zoom, overflow: "hidden", boxShadow: "0 6px 18px rgba(34,56,74,0.16)", touchAction: "none", background: "#fff" }}>
      <canvas ref={canvasRef} style={{ width: "100%", height: "100%", display: "block", pointerEvents: "none" }} />
      <div
        onPointerDown={(e) => begin(e, "move")}
        onPointerMove={move}
        onPointerUp={end}
        onPointerCancel={end}
        style={{
          position: "absolute", left: rect.x * zoom, top: rect.y * zoom, width: rect.w * zoom, height: rect.h * zoom,
          boxShadow: "0 0 0 9999px rgba(15,23,42,0.55)", outline: "2px solid #fff", cursor: "move", boxSizing: "border-box",
          backgroundImage: "linear-gradient(rgba(255,255,255,.35) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.35) 1px,transparent 1px)",
          backgroundSize: "33.333% 33.333%",
        }}
      >
        {HANDLES.map((h) => (
          <div
            key={h}
            data-handle={h}
            onPointerDown={(e) => begin(e, h)}
            onPointerMove={move}
            onPointerUp={end}
            onPointerCancel={end}
            style={{
              // วางชิดด้านใน เพราะกรอบอยู่ติดขอบภาพที่ overflow:hidden จะตัดมุมครึ่งหนึ่งทิ้ง
              position: "absolute", ...POS[h], width: 16, height: 16,
              marginLeft: POS[h].left === "100%" ? -16 : POS[h].left === 0 ? 0 : -8,
              marginTop: POS[h].top === "100%" ? -16 : POS[h].top === 0 ? 0 : -8,
              background: "#fff", border: "2px solid #0c76e5", borderRadius: 3, touchAction: "none",
            }}
          />
        ))}
      </div>
    </div>
  );
}
